import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { MessageService } from "primeng/api";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Globals } from "../app.globals";
import { CanNavDeactivateService } from "./nav.deactivate.service";

@Injectable({ providedIn: "root" })
export class UserService {
    user: User;
    isLoggedIn: boolean;

    idleState = "Not started.";
    timedOut = false;
    lastPing?: Date = null;
    timeoutCanceler;

    constructor(
        public http: HttpClient,
        private globals: Globals,
        private idle: Idle,
        private messageService: MessageService,
        private canNavDeactivateService: CanNavDeactivateService
    ) {}

    getApiUrl(): string {
        return "/api/authorization";
    }

    loginUser(email: string, password: string): Observable<User> {
        let data: any = {
            email: email,
            password: password,
        };

        return this.http.post(this.getApiUrl() + "/login", data).pipe(
            map((res) => {
                this.globals.hideSpinner();
                return res as User;
            }),
            catchError(this.handleError)
        );
    }

    getUser(): Observable<User> {
        return this.http.post(this.getApiUrl() + "/isLoggedIn", null).pipe(
            map((res) => res as User),
            catchError(this.handleError)
        );
    }

    verifyLoggedIn(): boolean {
        return this.isLoggedIn;
    }

    setUser(user): void {
        this.user = user;
        this.isLoggedIn = true;
    }

    resetPassword(oldPassword: string, newPassword: string): Observable<any> {
        let data = {
            oldPassword: oldPassword,
            newPassword: newPassword,
        };

        return this.http
            .put(this.getApiUrl() + "/reset", data)
            .pipe(catchError(this.handleError));
    }

    private handleError(error: any): Observable<any> {
        return throwError(
            error.error || error || "Server error, please refresh and try again"
        );
    }

    logout(): Observable<any> {
        this.isLoggedIn = false;
        this.user = null;
        this.resetIdleWarning();

        return this.http.post("/logout", null);
    }

    canAccessProject(
        section: string,
        projectId: number
    ): Observable<ProcessActivity> {
        return this.http
            .get(this.getApiUrl() + "/" + section + "/" + projectId)
            .pipe(map((res) => res as ProcessActivity));
    }

    getAllProjectAccesses(
        section: string
    ): Observable<number[]> {
        return this.http
            .get(this.getApiUrl() + "/" + section + "/projectaccess")
            .pipe(map((res) => res as number[]));
    }

    canAccessGeneralDocuments(section: string): Observable<ProcessActivity> {
        return this.http
            .get(this.getApiUrl() + "/" + section + "/documents")
            .pipe(map((res) => res as ProcessActivity));
    }

    canAccessLocation(
        section: string,
        projectId?: number
    ): Promise<ProcessActivity> {
        return this.http
            .get(this.getApiUrl() + "/" + section + "/" + projectId)
            .pipe(map((res) => res as ProcessActivity))
            .toPromise();
    }

    hasAccessToProject(
        section: string,
        projectId: number
    ): Observable<HasAccess> {
        return this.http
            .get(this.getApiUrl() + "/hasaccess/" + section + "/" + projectId)
            .pipe(map((res) => res as HasAccess));
    }

    setInactivityTimer(idleTime, timeout: number = 60) {
        if (!this.isLoggedIn) return;

        this.resetIdleWarning();

        this.idle.setIdle(idleTime);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        this.idle.onIdleEnd.subscribe(() => {});

        this.idle.onTimeout.subscribe(() => {
            this.messageService.clear();
        });

        this.idle.onIdleStart.subscribe(() => {
            this.resetIdleWarning();

            this.messageService.add({
                key: "custom",
                sticky: true,
                severity: "warn",
                summary: "",
                detail: "You will be logged out shortly for security purposes. If you wish to remain logged in, please click Continue below.",
            });

            this.timeoutCanceler = setTimeout(() => {
                this.canNavDeactivateService.ignore = true;
                this.resetIdleWarning();
                window.onbeforeunload = null;
                window.location.href = "/login";
            }, timeout * 1000);
        });

        this.idle.watch();
    }

    resetIdleWarning() {
        this.messageService.clear();

        if (this.timeoutCanceler) {
            clearTimeout(this.timeoutCanceler);
            this.timeoutCanceler = null;
        }

        this.idle.stop();
    }

    onRejectUserActivity() {
        this.resetIdleWarning();
        this.idle.watch();
    }

    setClient(name: string): Observable<User> {
        return this.http
            .post(this.getApiUrl() + "/setClient/" + name, null)
            .pipe(
                map((res) => res as User),
                catchError(this.handleError)
            );
    }

    pauseAutoLogoff() {
        this.idle.stop();
    }

    unpauseAutoLogoff() {
        this.idle.watch(true);
    }

    hasAccessType(formArea: string, accessTypes: string[], projectId: number) {
        return this.http
            .post<boolean>(
                this.getApiUrl() +
                    "/" +
                    formArea +
                    "/has-access-type/" +
                    !isNaN(projectId),
                accessTypes
            )
            .toPromise();
    }

    checkProjectAccess(accessNbr: number) {
        return this.http
            .post<boolean>(
                this.getApiUrl() +
                "/project-access",
                accessNbr
            )
            .toPromise();
    }
}

export class HasAccess {
    access: boolean;
    message: string;
}

export class ProcessActivity {
    activity: string;
    process: string;
}

class User {
    userId: number;
    fullName: string;
    email: string;
    role: string;
    status: string;
    employeeNumber: string;
    processes: ProcessActivity[];
    numberOfAssignedClients: number;
    currentClient: string;
}
